import React, { useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { FiRefreshCcw, FiSend } from "react-icons/fi";
import {
  MdOutlineTrendingUp,
  MdOutlineCheckCircle,
  MdOutlineHistory,
  MdAccountBalanceWallet,
  MdPeopleOutline,
} from "react-icons/md";
import { HiOutlineUsers } from "react-icons/hi";
import { RiUserSettingsLine } from "react-icons/ri";

import {
  TransactionsApi,
  CustomersApi,
  UsersApi,
  AgentBalancesApi,
} from "../../../api/common/apiIndex";
import { useAuth } from "../../../context/AuthProvider";
import { useIsAdmin } from "../hooks/useIsAdmin";
import StatusBadge from "../transactions/StatusBadge";
import Loader from "../../common/commonUtils/Loader";

import "./BusinessOverviewDashboard.css";

const numberFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const moneyFormatter = (amount, currency = "SEK", opts = {}) => {
  if (typeof amount !== "number" || Number.isNaN(amount)) return "—";
  try {
    return amount.toLocaleString("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      ...opts,
    });
  } catch {
    return `${amount.toLocaleString("en-US", { maximumFractionDigits: 0 })} ${currency}`;
  }
};

const formatNumber = (value) => numberFormatter.format(value ?? 0);

const IN_PROGRESS = ["PROCESSING", "READY"];
const PAID = ["PAID"];
const LOW_BALANCE_THRESHOLD = 100;
const EMPTY_BALANCE_STATS = Object.freeze({
  totalsByCurrency: [],
  fundedAgents: 0,
  countries: 0,
  lowBalanceAgents: [],
  totalAvailable: 0,
});

export default function BusinessOverviewDashboard() {
  const { hasRole, user } = useAuth();
  const isBusinessAdmin = useIsAdmin();
  const isAgent = Boolean(hasRole?.("AGENT") && !isBusinessAdmin);
  const assignedTo = isAgent ? "me" : undefined;
  const scopeKey = isAgent ? "mine" : "business";
  const canViewOrgMetrics = !isAgent;
  const queryClient = useQueryClient();

  const transactionsSummaryQuery = useQuery({
    queryKey: ["business-dashboard", "transactions-summary", { scope: scopeKey }],
    queryFn: async () => {
      const fetchCount = (status) =>
        TransactionsApi.list({
          page: 0,
          size: 1,
          sort: "createdAt,desc",
          status,
          assignedTo,
        }).then((res) => res?.totalElements ?? 0);

      const [total, inProgress, paid] = await Promise.all([
        fetchCount(undefined),
        fetchCount(IN_PROGRESS),
        fetchCount(PAID),
      ]);

      return { total, inProgress, paid };
    },
    staleTime: 1000 * 60 * 2,
  });

  const recentTransactionsQuery = useQuery({
    queryKey: ["business-dashboard", "recent-transactions", { scope: scopeKey }],
    queryFn: () =>
      TransactionsApi.list({
        page: 0,
        size: 5,
        sort: "createdAt,desc",
        assignedTo,
      }),
    select: (page) => page?.content ?? [],
    staleTime: 1000 * 60,
  });

  const customersCountQuery = useQuery({
    queryKey: ["business-dashboard", "customers-count"],
    queryFn: () =>
      CustomersApi.list({
        page: 0,
        size: 1,
        sort: "createdAt,desc",
      }),
    select: (page) => page?.totalElements ?? 0,
    staleTime: 1000 * 60 * 5,
    enabled: canViewOrgMetrics,
  });

  const agentsCountQuery = useQuery({
    queryKey: ["business-dashboard", "agents-count"],
    queryFn: () =>
      UsersApi.listAgents({
        page: 0,
        size: 1,
        sort: "firstName,asc",
      }),
    select: (page) => page?.totalElements ?? 0,
    staleTime: 1000 * 60 * 5,
    enabled: canViewOrgMetrics,
  });

  const balancesQuery = useQuery({
    queryKey: ["business-dashboard", "agent-balances"],
    queryFn: () =>
      AgentBalancesApi.list({
        page: 0,
        size: 500,
        sort: "agentId,asc",
      }),
    staleTime: 1000 * 60 * 5,
    enabled: canViewOrgMetrics,
  });

  const balanceStats = useMemo(() => {
    if (!canViewOrgMetrics) return EMPTY_BALANCE_STATS;

    const rows = balancesQuery.data?.content ?? [];
    if (!rows.length) {
      return EMPTY_BALANCE_STATS;
    }

    const perCurrency = new Map();
    let fundedAgents = 0;
    const lowBalanceAgents = [];
    const countries = new Set();
    let totalAvailable = 0;

    rows.forEach((row) => {
      const currency = (row?.currency || "SEK").toUpperCase();
      const available = Number(row?.available ?? 0);
      const reserved = Number(row?.reserved ?? 0);
      const country = (row?.country || "").toUpperCase();

      if (country) countries.add(country);
      if (available > 0) fundedAgents += 1;
      if (available <= LOW_BALANCE_THRESHOLD) {
        lowBalanceAgents.push({
          id: row?.agentId,
          uuid: row?.agentUuid,
          name: row?.agentName || `Agent #${row?.agentId ?? "?"}`,
          available,
          currency,
          country: row?.country,
        });
      }

      const current = perCurrency.get(currency) || { available: 0, reserved: 0 };
      current.available += available;
      current.reserved += reserved;
      perCurrency.set(currency, current);

      totalAvailable += available;
    });

    lowBalanceAgents.sort((a, b) => a.available - b.available);

    const totalsByCurrency = Array.from(perCurrency.entries())
      .map(([currency, totals]) => ({
        currency,
        available: totals.available,
        reserved: totals.reserved,
      }))
      .sort((a, b) => b.available - a.available);

    return {
      totalsByCurrency,
      fundedAgents,
      countries: countries.size,
      lowBalanceAgents: lowBalanceAgents.slice(0, 4),
      totalAvailable,
    };
  }, [balancesQuery.data, canViewOrgMetrics]);

  const successRate =
    transactionsSummaryQuery.data?.total > 0
      ? Math.round((transactionsSummaryQuery.data.paid / transactionsSummaryQuery.data.total) * 100)
      : 0;

  const transactionSummary = useMemo(
    () => ({
      total: transactionsSummaryQuery.data?.total ?? 0,
      inProgress: transactionsSummaryQuery.data?.inProgress ?? 0,
      paid: transactionsSummaryQuery.data?.paid ?? 0,
      successRate,
    }),
    [transactionsSummaryQuery.data, successRate]
  );

  const statCards = useMemo(() => {
    const cards = [];

    if (canViewOrgMetrics) {
      cards.push(
        {
          key: "customers",
          title: "Customers",
          value: customersCountQuery.data ?? 0,
          caption: customersCountQuery.isError
            ? "Unable to load customers."
            : "Active senders & recipients",
          Icon: HiOutlineUsers,
          accent: "bg-info-subtle text-info-emphasis",
        },
        {
          key: "agents",
          title: "Agents",
          value: agentsCountQuery.data ?? 0,
          caption:
            agentsCountQuery.data > 0
              ? `${balanceStats.fundedAgents} funded right now`
              : "Invite agents to get started",
          Icon: RiUserSettingsLine,
          accent: "bg-secondary-subtle text-secondary-emphasis",
        },
        {
          key: "balances",
          title: "Available Float",
          value:
            balanceStats.totalsByCurrency.length > 0
              ? moneyFormatter(
                balanceStats.totalsByCurrency[0].available,
                balanceStats.totalsByCurrency[0].currency
              )
              : "—",
          caption:
            balanceStats.totalsByCurrency.length > 1
              ? balanceStats.totalsByCurrency
                .slice(1, 3)
                .map((entry) => `${entry.currency} ${formatNumber(entry.available)}`)
                .join(" • ")
              : balanceStats.totalsByCurrency.length === 1
                ? `${balanceStats.totalsByCurrency[0].currency} float`
                : "No balances yet",
          Icon: MdAccountBalanceWallet,
          accent: "bg-dark-subtle text-dark-emphasis",
          rawValue: balanceStats.totalAvailable,
        }
      );
    }

    return cards;
  }, [
    customersCountQuery.data,
    customersCountQuery.isError,
    agentsCountQuery.data,
    balanceStats,
    canViewOrgMetrics,
  ]);

  const quickLinks = [
    {
      key: "new-transfer",
      label: "New transfer",
      description: "Register a payout in minutes",
      to: "/home/transfer",
      Icon: FiSend,
      accent: "bg-success-subtle text-success-emphasis",
      adminOnly: true,
    },
    {
      key: "transactions",
      label: "Transfers queue",
      description: "Track statuses and SLA",
      to: "/home/transactions",
      Icon: MdOutlineHistory,
      accent: "bg-warning-subtle text-warning-emphasis",
    },
    {
      key: "customers",
      label: "Customers",
      description: "Manage senders & recipients",
      to: "/home/customers",
      Icon: HiOutlineUsers,
      accent: "bg-info-subtle text-info-emphasis",
    },
    {
      key: "agents",
      label: "Agents",
      description: "Allocate float & access",
      to: "/home/agents",
      Icon: RiUserSettingsLine,
      accent: "bg-secondary-subtle text-secondary-emphasis",
      adminOnly: true,
    },
  ];
  if (canViewOrgMetrics) {
    quickLinks.push(
      {
        key: "balances",
        label: "Country balances",
        description: "Monitor available float",
        to: "/home/balance",
        Icon: MdAccountBalanceWallet,
        accent: "bg-dark-subtle text-dark-emphasis",
        adminOnly: true,
      },
      {
        key: "team",
        label: "Team",
        description: "Manage your teammates",
        to: "/home/team",
        Icon: MdPeopleOutline,
        accent: "bg-primary-subtle text-primary-emphasis",
        adminOnly: true,
      }
    );
  } else {
    quickLinks.push({
      key: "agent-balance",
      label: "My balance",
      description: "Check your float allocation",
      to: `/home/agent/${user?.uuid ?? ""}`,
      Icon: MdAccountBalanceWallet,
      accent: "bg-dark-subtle text-dark-emphasis",
      agentOnly: true,
    });
  }
  const visibleQuickLinks = quickLinks.filter((card) => {
    if (card.adminOnly) return canViewOrgMetrics;
    if (card.agentOnly) return !canViewOrgMetrics;
    return true;
  });

  const renderCardValue = (card) => {
    if (card.key === "balances") {
      if (balancesQuery.isError) return "—";
      if (balancesQuery.isLoading && !balancesQuery.data) return "…";
      return card.value;
    }
    if (card.key === "customers" && customersCountQuery.isLoading && customersCountQuery.data === undefined)
      return "…";
    if (card.key === "agents" && agentsCountQuery.isLoading && agentsCountQuery.data === undefined)
      return "…";
    if (transactionsSummaryQuery.isError) return "—";
    if (transactionsSummaryQuery.isLoading && !transactionsSummaryQuery.data) return "…";
    return formatNumber(card.value);
  };

  const isRefreshing =
    transactionsSummaryQuery.isFetching ||
    recentTransactionsQuery.isFetching ||
    (canViewOrgMetrics &&
      (customersCountQuery.isFetching ||
        agentsCountQuery.isFetching ||
        balancesQuery.isFetching));

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["business-dashboard"] });
  }, [queryClient]);

  const recentTransfersTitle = isAgent ? "My recent transfers" : "Recent transfers";
  const recentTransfersSubtitle = isAgent
    ? "Latest transfers assigned to you"
    : "Latest five transactions in your business";
  const emptyRecentCopy = isAgent ? "No transfers assigned to you yet." : "No transfers recorded yet.";
  const recentColumnClass = canViewOrgMetrics ? "col-12 col-xl-7" : "col-12";

  return (
    <div className="business-dashboard py-3">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
        <div>
          <h1 className="h4 mb-1">Business overview</h1>
          <p className="text-muted mb-0">
            Track transfers, customers, and agent liquidity from one place.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          <FiRefreshCcw className={isRefreshing ? "icon-spin" : ""} aria-hidden="true" />
          Refresh
        </button>
      </div>

      <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3 mb-4">
        {visibleQuickLinks.map((card) => (
          <div className="col" key={card.key}>
            <Link
              to={card.to}
              className="card border shadow-sm quick-card h-100 text-decoration-none text-reset"
            >
              <div className="card-body d-flex align-items-start gap-3">
                <span className={`quick-card-icon ${card.accent}`}>
                  <card.Icon aria-hidden="true" />
                </span>
                <div>
                  <div className="fw-semibold">{card.label}</div>
                  <p className="text-muted small mb-0">{card.description}</p>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>

      <div className="mb-4">
        <div className="card transaction-summary-card border-0 shadow-sm">
          <div className="card-body border shadow-sm d-flex flex-column flex-lg-row gap-3">
            {[
              {
                key: "total",
                label: isAgent ? "My Transfers" : "Total Transfers",
                caption: transactionsSummaryQuery.isError
                  ? "Unable to load transfers."
                  : `${formatNumber(transactionSummary.paid)} paid`,
                Icon: MdOutlineTrendingUp,
                accent: "bg-primary-subtle text-primary-emphasis",
                value: transactionSummary.total,
              },
              {
                key: "in-progress",
                label: isAgent ? "My Queue" : "In Progress",
                caption:
                  transactionSummary.inProgress > 0
                    ? "Transfers awaiting payout"
                    : "You're all caught up",
                Icon: MdOutlineHistory,
                accent: "bg-warning-subtle text-warning-emphasis",
                value: transactionSummary.inProgress,
              },
              {
                key: "paid",
                label: "Paid",
                caption:
                  transactionSummary.total > 0
                    ? `${transactionSummary.successRate}% success rate`
                    : "No transfers yet",
                Icon: MdOutlineCheckCircle,
                accent: "bg-success-subtle text-success-emphasis",
                value: transactionSummary.paid,
                derived: true,
              },
            ].map((metric) => (
              <div key={metric.key} className="flex-grow-1 d-flex align-items-center gap-3 transaction-summary-metric">
                <span className={`stat-card-icon ${metric.accent}`}>
                  <metric.Icon aria-hidden="true" />
                </span>
                <div className="flex-grow-1">
                  <div className="text-uppercase text-body-secondary fw-semibold small tracking-wide">
                    {metric.label}
                  </div>
                  <div className="fs-4 fw-semibold mt-1">
                    {transactionsSummaryQuery.isError
                      ? "—"
                      : transactionsSummaryQuery.isLoading && !transactionsSummaryQuery.data
                      ? "…"
                      : formatNumber(metric.value)}
                  </div>
                  <small className="text-muted">{metric.caption}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="row row-cols-1 row-cols-sm-2 row-cols-xl-3 g-3 mb-4">
        {statCards.map((card) => (
          <div className="col" key={card.key}>
            <div className="card stat-card h-100 border rounded-4 shadow-sm">
              <div className="card-body border shadow-sm d-flex flex-column gap-3">
                <div className="d-flex align-items-center justify-content-between gap-3">
                  <div className="flex-grow-1">
                    <div className="text-uppercase text-body-secondary fw-semibold small tracking-wide">
                      {card.title}
                    </div>
                    <div className="fs-4 fw-semibold mt-1">
                      {renderCardValue(card)}
                    </div>
                  </div>
                  <span className={`stat-card-icon ${card.accent}`}>
                    <card.Icon aria-hidden="true" />
                  </span>
                </div>
                <small className="text-muted">{card.caption}</small>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3">
        <div className={recentColumnClass}>
          <div className="card border shadow-sm h-100">
            <div className="card-header bg-transparent d-flex align-items-center justify-content-between">
              <div>
                <h2 className="h6 mb-0">{recentTransfersTitle}</h2>
                <small className="text-muted">{recentTransfersSubtitle}</small>
              </div>
              <Link to="/home/transactions" className="btn btn-link btn-sm text-decoration-none px-0">
                View all
              </Link>
            </div>
            <div className="card-body">
              {recentTransactionsQuery.isLoading && !recentTransactionsQuery.data?.length ? (
                <Loader text="Loading transfers..." />
              ) : null}

              {recentTransactionsQuery.isError ? (
                <div className="text-muted">Unable to load recent transfers.</div>
              ) : null}

              {!recentTransactionsQuery.isLoading &&
                !recentTransactionsQuery.isError &&
                recentTransactionsQuery.data?.length === 0 ? (
                <div className="text-muted">{emptyRecentCopy}</div>
              ) : null}

              {recentTransactionsQuery.data?.length ? (
                <ul className="list-group list-group-flush">
                  {recentTransactionsQuery.data.map((tx) => (
                    <li
                      key={tx?.uuid ?? tx?.id}
                      className="list-group-item px-0 d-flex align-items-start justify-content-between gap-3 flex-wrap"
                    >
                      <div className="flex-grow-1">
                        <Link
                          to={`/home/transaction/${tx?.uuid ?? tx?.id}`}
                          className="fw-semibold text-decoration-none"
                        >
                          {tx?.senderName ?? "Sender"} → {tx?.recipientName ?? "Recipient"}
                        </Link>
                        <div className="text-muted small">
                          {tx?.recipientCountry ? `${tx.recipientCountry} • ` : ""}
                          {tx?.createdAt
                            ? new Date(tx.createdAt).toLocaleString("en-US", {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                            : "—"}
                        </div>
                      </div>
                      <div className="d-flex flex-column align-items-end text-end gap-1">
                        <div className="fw-semibold">
                          {typeof tx?.receiveAmount === "number"
                            ? tx.receiveAmount.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                            : tx?.receiveAmount}{" "}
                          {tx?.receiveCurrency}
                        </div>
                        <StatusBadge status={tx?.status} />
                      </div>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </div>

        {canViewOrgMetrics ? (
          <div className="col-12 col-xl-5">
            <div className="card border shadow-sm h-100">
              <div className="card-header bg-transparent d-flex align-items-center justify-content-between">
                <div>
                  <h2 className="h6 mb-0">Agent balance health</h2>
                  <small className="text-muted">
                    Funded agents across {balanceStats.countries} countries
                  </small>
                </div>
                <Link to="/home/balance" className="btn btn-link btn-sm text-decoration-none px-0">
                  Manage balances
                </Link>
              </div>
              <div className="card-body">
                {balancesQuery.isLoading && !balancesQuery.data ? <Loader text="Loading balances..." /> : null}
                {balancesQuery.isError ? (
                  <div className="text-muted">Unable to fetch balance data right now.</div>
                ) : null}

                {balanceStats.totalsByCurrency.length ? (
                  <div className="mb-3">
                    <div className="fw-semibold mb-1">Total available</div>
                    <div className="d-flex flex-wrap gap-2">
                      {balanceStats.totalsByCurrency.map((entry) => (
                        <span
                          key={entry.currency}
                          className="badge rounded-pill  border text-body"
                        >
                          {moneyFormatter(entry.available, entry.currency, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <div className="fw-semibold">{balanceStats.fundedAgents}</div>
                    <small className="text-muted">Funded agents</small>
                  </div>
                  <div className="text-end">
                    <div className="fw-semibold">{balanceStats.countries}</div>
                    <small className="text-muted">Countries covered</small>
                  </div>
                </div>

                {balanceStats.lowBalanceAgents.length ? (
                  <div>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <strong className="small text-uppercase text-body-secondary">
                        Low balance alerts
                      </strong>
                      <Link to="/home/add-balance" className="btn btn-sm btn-outline-secondary">
                        Fund agent
                      </Link>
                    </div>
                    <ul className="list-group list-group-flush">
                      {balanceStats.lowBalanceAgents.map((agent) => (
                        <li
                          key={agent.uuid ?? agent.id}
                          className="list-group-item px-0 d-flex align-items-center justify-content-between gap-2"
                        >
                          <div className="flex-grow-1">
                            <Link
                              to={`/home/agent/${agent.uuid ?? agent.id}`}
                              className="fw-semibold text-decoration-none"
                            >
                              {agent.name}
                            </Link>
                            <div className="text-muted small">
                              {agent.country ? agent.country.toUpperCase() : "—"}
                            </div>
                          </div>
                          <span className="badge text-bg-warning-subtle text-warning-emphasis border border-warning-subtle">
                            {moneyFormatter(agent.available, agent.currency, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="text-muted small">
                    All agents are above the {LOW_BALANCE_THRESHOLD} threshold.
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
